<template>
  <div v-if="showIsAnalysisActiveStatus && analysisNotice">
    <div
      data-qa="analysis_name"
      class="text-body-1 text-secondary--text"
    >
      {{ analysis.name.replaceAll('&nbsp;', ' ').replaceAll('&amp;nbsp;', ' ') }}
    </div>
    <div
      v-if="analysis.government_code"
      class="text--secondary text-caption mt-1"
    >
      {{ analysis.government_code }}
    </div>
    <div class="d-flex justify-space-between">
      <div class="text-body-2 text-error--text mt-2">
        {{ analysisNotice }}
      </div>
      <CartButton
        v-if="analysis.is_in_cart && hasUserPermission && showCartButton"
        :is-in-cart="analysis.is_in_cart"
        :partner="partner"
        :disable-cart-button="disableCartButton"
        :analysis-id="analysis.id"
        :analysis-synonym-id="analysis.analysis_synonym_id"
        :biomaterial-id="analysis.biomaterial_id"
        @click:delete-from-cart="handleDeleteFromCart"
      />
    </div>
  </div>
  <div
    v-else
    :class="{ 'd-flex justify-space-between align-center': !isMobile }"
  >
    <div class="d-flex flex-column">
      <div
        data-qa="analysis_name"
        class="text-body-1 text--text"
      >
        {{ analysis.name.replaceAll('&nbsp;', ' ').replaceAll('&amp;nbsp;', ' ') }}
      </div>
      <div
        v-if="analysis.government_code"
        class="text--secondary text-caption mt-1"
      >
        {{ analysis.government_code }}
      </div>
      <div
        data-qa="analysis_details"
        class="mt-2"
        :class="{ 'mt-2 d-flex align-center': !isMobile }"
      >
        <AnalysisDetail
          data-qa="analysis_execution_days"
          icon="ui-icon-clock"
          :text="executionDaysText"
          class="mr-4"
        />

        <div
          v-if="!isMobile && analysis.biomaterial_name"
          class="mr-4 bg-gray-80--text"
        >
          &bull;
        </div>

        <AnalysisDetail
          v-if="analysis.biomaterial_name"
          data-qa="analysis_biomaterial"
          icon="ui-icon-test-tube"
          :class="{ 'mt-2': isMobile }"
          :text="analysis.biomaterial_name"
        />
      </div>
    </div>

    <div class="d-flex justify-space-between mt-2">
      <PriceBlock
        :style="{ width: isMobile ? 'initial' : 'max-content', minWidth: '120px' }"
        :price="analysis.price"
        :price_with_discount="analysis.price_with_discount"
      />
      <CartButton
        v-if="hasUserPermission && showCartButton"
        :is-in-cart="analysis.is_in_cart"
        :analysis-id="analysis.id"
        :analysis-synonym-id="analysis.analysis_synonym_id"
        :biomaterial-id="analysis.biomaterial_id"
        :partner="partner"
        :class="{ 'ml-6': !isMobile }"
        :disable-cart-button="disableCartButton"
        @click:add-to-cart="handleAddToCart"
        @click:delete-from-cart="handleDeleteFromCart"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, Ref } from 'vue'
import { declensionByNum, axiosClient, dispatchCustomEventGlobally } from 'www/utils'
import { LOGIN_LINKS } from 'www/constants/links'
import { Analysis, Partner } from 'www/vue_components/common/Analyses/types'
import useCommonAnalysesStore from 'www/vue_components/common/Analyses/stores/useCommonStore'
import AnalysisDetail from 'www/vue_components/common/Analyses/components/AnalysisCard/components/AnalysisDetail.vue'
import PriceBlock from 'www/vue_components/common/Analyses/components/PriceBlock/PriceBlock.vue'
import CartButton from 'www/vue_components/common/Analyses/components/AnalysisCard/components/CartButton.vue'

const emit = defineEmits(['click:add-to-cart', 'click:delete-from-cart'])

const props = withDefaults(defineProps<{
  partner: Partner
  analysis: Analysis
  showCartButton: boolean
  showIsAnalysisActiveStatus?: boolean
  showIsDuplicateDescription?: boolean
  disableCartButton: boolean
}>(), {
  analysis: () => ({
    id: 0,
    analysis_synonym_id: 0,
    biomaterial_id: 0,
    collection_service_id: 0,
    name: '',
    price: 0,
    price_with_discount: 0,
    execution_days: 0,
    biomaterial_name: '',
    is_active: false,
    is_in_cart: false,
    is_duplicate: false,
  }),
  partner: () => ({
    id: 0,
    slug: '',
    name: '',
    logo: '',
    discount: 0,
    analyses: [],
  }),
  showCartButton: false,
  showIsAnalysisActiveStatus: false,
  showIsDuplicateDescription: false,
})

const commonAnalysesStore = useCommonAnalysesStore()

const isMobile = window.MOBILE_VERSION
const { isDoctor } = window.USER

const executionDaysText = computed(() => {
  if (props.analysis.execution_days === 0) {
    return ''
  }

  return `${props.analysis.execution_days} ${declensionByNum(props.analysis.execution_days, ['день', 'дня', 'дней'])}`
})

const analysisNotice = computed(() => {
  if (props.showIsDuplicateDescription && props.analysis.is_duplicate) {
    return 'Лаборатория не сможет принять в этом заказе. Оформите анализ в отдельном заказе — сохранили его в корзине'
  }

  if (!props.analysis.is_active) {
    return 'Лаборатория не готова принять анализ'
  }

  return ''
})

const hasUserPermission: Ref<boolean> = computed(() => (commonAnalysesStore.isUserHasPermission || isDoctor))

function handleAddToCart() {
  if (isDoctor) {
    dispatchCustomEventGlobally('dialog:open', {
      maxWidth: 304,
      noClickAnimation: true,
      title: 'Войдите в профиль пациента, чтобы продолжить',
      confirmText: 'Войти',
      closeText: 'Отменить',
      attachSelector: '#use-vue-base-dialog',
      isDanger: true,
      beforeConfirm: async () => {
        await axiosClient.get('/profile/logout/')
        window.location.assign(LOGIN_LINKS.medtochka)
      },
    })

    return
  }

  emit('click:add-to-cart')
}

function handleDeleteFromCart() {
  if (isDoctor) {
    dispatchCustomEventGlobally('dialog:open', {
      maxWidth: 304,
      noClickAnimation: true,
      title: 'Войдите в профиль пациента, чтобы продолжить',
      confirmText: 'Войти',
      closeText: 'Отменить',
      attachSelector: '#use-vue-base-dialog',
      isDanger: true,
      beforeConfirm: async () => {
        await axiosClient.get('/profile/logout/')
        window.location.assign(LOGIN_LINKS.medtochka)
      },
    })

    return
  }

  emit('click:delete-from-cart')
}
</script>
